import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const NavIcon = ({ type }) => {
  const icons = {
    dashboard: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
    products: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
    inventory: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 7h-9"/><path d="M14 17H5"/><circle cx="17" cy="17" r="3"/><circle cx="7" cy="7" r="3"/></svg>,
    invoice: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
    purchase: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>,
    suppliers: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
    customers: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    report: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
    alert: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
    warehouse: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
    logout: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  };
  return <span className="nav-icon">{icons[type]}</span>;
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };

  const navItems = [
    { section: 'Main', items: [
      { to: '/', icon: 'dashboard', label: 'Dashboard', end: true },
    ]},
    { section: 'Billing', items: [
      { to: '/invoices', icon: 'invoice', label: 'Invoices' },
      { to: '/invoices/new', icon: 'invoice', label: 'New Invoice', roles: ['admin','billing_staff'] },
      { to: '/eway-bills', icon: 'suppliers', label: 'E-way Bills' },
    ]},
    { section: 'Inventory', items: [
      { to: '/products', icon: 'products', label: 'Products' },
      { to: '/inventory', icon: 'inventory', label: 'Stock Overview' },
      { to: '/inventory/scan', icon: 'products', label: 'Barcode Lookup' },
      { to: '/inventory/near-expiry', icon: 'alert', label: 'Near Expiry' },
      { to: '/inventory/low-stock', icon: 'alert', label: 'Low Stock' },
      { to: '/inventory/movements', icon: 'warehouse', label: 'Stock Ledger' },
    ]},
    { section: 'Purchases', items: [
      { to: '/purchases/orders', icon: 'purchase', label: 'Purchase Orders' },
      { to: '/purchases/grn', icon: 'warehouse', label: 'Goods Received' },
    ]},
    { section: 'Masters', items: [
      { to: '/suppliers', icon: 'suppliers', label: 'Suppliers' },
      { to: '/customers', icon: 'customers', label: 'Customers' },
    ]},
    { section: 'Reports', items: [
      { to: '/reports/gst', icon: 'report', label: 'GST Reports' },
      { to: '/reports/sales', icon: 'report', label: 'Sales Report' },
      { to: '/reports/stock', icon: 'report', label: 'Stock Valuation' },
    ]},
  ];

  return (
    <div className="app-layout">
      {sidebarOpen && <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:40 }}
        onClick={() => setSidebarOpen(false)} />}
      
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-logo">
          <div className="logo-icon">A+</div>
          <div>
            <h1>Augurs Healthcare</h1>
            <span>Pharma ERP</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          {navItems.map(section => (
            <div className="nav-section" key={section.section}>
              <div className="nav-section-title">{section.section}</div>
              {section.items
                .filter(item => !item.roles || item.roles.includes(user?.role))
                .map(item => (
                  <NavLink key={item.to} to={item.to} end={item.end}
                    className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                    onClick={() => setSidebarOpen(false)}>
                    <NavIcon type={item.icon} />
                    {item.label}
                  </NavLink>
                ))
              }
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-avatar">{user?.full_name?.charAt(0) || 'U'}</div>
          <div className="user-info">
            <div className="user-name">{user?.full_name}</div>
            <div className="user-role">{user?.role?.replace('_', ' ')}</div>
          </div>
          <button onClick={handleLogout} title="Logout" style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px',
            background: 'rgba(220,53,69,0.15)', border: '1px solid rgba(220,53,69,0.35)',
            borderRadius: 6, color: '#f8d7da', fontSize: 12, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(220,53,69,0.4)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(220,53,69,0.15)'; }}
          >
            <NavIcon type="logout" />
            Logout
          </button>
        </div>
      </aside>

      <div className="main-content">
        <header className="topbar">
          <button className="btn btn-icon mobile-menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
          <div className="text-sm text-secondary">
            {user?.company_name} {user?.warehouse_name ? `• ${user.warehouse_name}` : ''}
          </div>
          <div className="flex items-center gap-3">
            <span className="badge badge-green" style={{ fontSize: 11 }}>
              FY {new Date().getMonth() >= 3 ? new Date().getFullYear() : new Date().getFullYear() - 1}-{String((new Date().getMonth() >= 3 ? new Date().getFullYear() : new Date().getFullYear() - 1) + 1).slice(2)}
            </span>
          </div>
        </header>
        <main className="page-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
